import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { NextStepsChecklist } from '@/customer/components/next-steps-checklist'
import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { StackedHeadline } from '@/customer/components/stacked-headline'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useOnboardingIssues } from '@/customer/hooks/use-onboarding-issues'
import { routes } from '@/customer/routes'

/**
 * Post-subscribe welcome. Reached via `navigate()` from the checkout
 * page after a successful subscription creation.
 *
 * Only renders the celebration when the subscription is healthy.
 * Any other state shows a generic fallback with a link home.
 */
export function WelcomePage() {
  useDocumentTitle('Welcome to Snoots')
  const { pet } = useOutletContext<PetScopeContext>()
  const navigate = useNavigate()
  const me = useGetMe()
  const onboarding = useOnboardingIssues()

  if (me.isPending || !me.data) {
    return (
      <PageContainer className="items-center justify-center gap-6">
        <LoadingSpinner />
      </PageContainer>
    )
  }

  const issues = onboarding.issues.filter((issue) => issue.type !== 'no_onboarded_pet')

  return (
    <PageContainer className="relative gap-10">
      <div className="relative flex flex-col gap-6 py-4">
        <Confetti />
        <StackedHeadline
          lines={[
            { text: 'Welcome to', color: 'yellow' },
            { text: 'the family,', color: 'pink' },
            { text: pet.name, color: 'green' },
          ]}
        />
      </div>

      <div className="text-headline-sm text-foreground">
        You&apos;re all set - <span className="font-bold">{pet.name}&apos;s</span> membership is
        active.
      </div>

      {issues.length > 0 ? (
        <NextStepsChecklist
          issues={issues}
          description={`A few things to complete before booking for ${pet.name}.`}
          replaceOnNavigate
        />
      ) : null}

      <Button
        type="button"
        onClick={() => navigate(routes.account.index, { replace: true })}
        className="h-14 w-full"
      >
        Continue to member portal
        <ArrowRight className="size-5" />
      </Button>
    </PageContainer>
  )
}

/** Brand palette used by the confetti burst. */
const CONFETTI_COLORS = [
  'var(--brand-yellow)',
  'var(--brand-pink)',
  'var(--brand-blue)',
  'var(--brand-baby-blue)',
  'var(--brand-green)',
  'var(--brand-orange)',
]

const CONFETTI_COUNT = 40

/**
 * One-shot confetti burst rained down over the headline.
 *
 * Hand-rolled via framer-motion: each particle is a small coloured
 * square that falls from just above the container, drifts sideways,
 * and rotates as it goes. The particle set is memoised so it stays
 * stable across re-renders - the animation plays exactly once per
 * mount. Absolutely positioned + `pointer-events-none` so it never
 * blocks interaction with the content underneath.
 */
function Confetti() {
  const particles = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
        id: i,
        startX: Math.random() * 100,
        drift: (Math.random() - 0.5) * 60,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
        size: 6 + Math.random() * 6,
        rotation: Math.random() * 720 - 360,
        duration: 2 + Math.random() * 1.5,
        delay: Math.random() * 0.35,
      })),
    []
  )

  return (
    <div className="pointer-events-none absolute inset-x-0 -top-4 h-[24rem] overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute top-0 rounded-[2px]"
          style={{
            left: `${p.startX}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
          }}
          initial={{ y: 0, x: 0, rotate: 0, opacity: 1 }}
          animate={{ y: 380, x: p.drift, rotate: p.rotation, opacity: [1, 1, 0] }}
          transition={{ duration: p.duration, ease: 'easeOut', delay: p.delay }}
        />
      ))}
    </div>
  )
}
